import { marked, type TokenizerAndRendererExtension, type Tokens } from 'marked';
import DOMPurify from 'dompurify';

/** Inline wiki-link token: `[[kind:id]]` or `[[kind:id|Label]]`. */
interface WikiLinkToken extends Tokens.Generic {
  type: 'wikiLink';
  ref: string;
  label: string;
}

const WIKILINK_RULE = /^\[\[\s*([a-z][a-z0-9-]*:[a-z0-9][a-z0-9-]*)\s*(?:\|\s*([^\]]+?)\s*)?\]\]/i;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * marked extension rendering `[[kind:id|label]]` wiki-links as
 * `<a class="wikilink" data-ref="kind:id">label</a>`. Navigation is wired up at runtime
 * by MarkdownView via the RefNav context (the anchor has no href until resolved).
 */
const wikiLinkExtension: TokenizerAndRendererExtension = {
  name: 'wikiLink',
  level: 'inline',
  start(src) {
    return src.indexOf('[[');
  },
  tokenizer(src) {
    const m = WIKILINK_RULE.exec(src);
    if (!m) return undefined;
    const token: WikiLinkToken = {
      type: 'wikiLink',
      raw: m[0],
      ref: m[1].toLowerCase(),
      label: (m[2] ?? '').trim(),
    };
    return token;
  },
  renderer(token) {
    const { ref, label } = token as WikiLinkToken;
    const text = label || ref;
    return `<a class="wikilink" data-ref="${escapeHtml(ref)}">${escapeHtml(text)}</a>`;
  },
};

marked.use({ breaks: true, gfm: true, extensions: [wikiLinkExtension] });

/** DOMPurify config that preserves wiki-link markers (`data-ref`). */
const SANITIZE_CONFIG = { ADD_ATTR: ['data-ref'] };

/** Render a block of markdown to sanitized HTML. */
export function renderMarkdown(src?: string): string {
  if (!src) return '';
  const html = marked.parse(src, { async: false }) as string;
  return DOMPurify.sanitize(html, SANITIZE_CONFIG);
}

/** Render a single line of markdown (no surrounding <p>). */
export function renderInline(src?: string): string {
  if (!src) return '';
  const html = marked.parseInline(src, { async: false }) as string;
  return DOMPurify.sanitize(html, SANITIZE_CONFIG);
}
